import type { AxiosInstance, InternalAxiosRequestConfig } from "axios";
import i18next from "i18next";

import { STORAGE_KEYS } from "@shared/constants/storageKeys";
import { InfoAlert, suppressServerErrorPopups } from "@shared/ui/alerts";

/**
 * Centraliza los headers de auth + idioma para los clientes axios compartidos.
 *
 * Reemplaza el patrón de pasar `Config()` en cada llamada: las funciones de api/
 * ya no necesitan setear headers a mano. Es aditivo y defensivo: nunca pisa un
 * header Authorization seteado explícitamente por quien llama, y no rompe cuando
 * no hay usuario logueado (endpoints públicos).
 */

/**
 * Forma minima que el interceptor necesita.
 *
 * Deliberadamente mas laxa que `InternalAxiosRequestConfig`: los tests lo ejercitan con
 * objetos planos — incluido `{}` sin headers, que cubre la rama defensiva — y esos tests
 * quedan sin tocar en la migracion.
 */
interface AuthRequestConfig {
  headers?: Record<string, unknown>;
}

/** Lo que el interceptor garantiza a la salida: headers siempre presente. */
interface ResolvedAuthRequestConfig extends AuthRequestConfig {
  headers: Record<string, unknown>;
}

/** Lee el JWT de localStorage; devuelve null si no existe o está malformado. */
export const readUserToken = (): string | null => {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return null;
    const { token } = JSON.parse(raw) as { token?: string };
    if (!token || token === "") return null;
    return token.replaceAll('"', "");
  } catch {
    return null;
  }
};

/** Interceptor de request: adjunta Authorization y Accept-Language si están disponibles. */
export const attachAuthHeaders = (config: AuthRequestConfig): ResolvedAuthRequestConfig => {
  const resolved = config as ResolvedAuthRequestConfig;
  resolved.headers = config.headers || {};

  const token = readUserToken();
  if (token && !resolved.headers.Authorization) {
    resolved.headers.Authorization = `Bearer ${token}`;
  }

  const language = localStorage.getItem("language");
  if (language && !resolved.headers["Accept-Language"]) {
    resolved.headers["Accept-Language"] = language;
  }

  return resolved;
};

/** Forma mínima del error de axios que el interceptor necesita mirar. */
interface AuthErrorResponse {
  config?: { url?: string };
  response?: { status?: number };
}

/**
 * Un 401 de `/v1/auth/**` no es una sesión vencida: esas rutas están en el whitelist de
 * `SecurityConfig`, así que el único 401 que pueden devolver es el de un login con
 * credenciales incorrectas. Tratarlo como expiración recargaría la pantalla y el colaborador
 * nunca leería el mensaje que le explica que erró la contraseña.
 */
const isAuthEndpoint = (url?: string): boolean => /(^|\/)auth\//.test(url ?? "");

/**
 * Borra las mismas dos claves que ya borra el corte por inactividad de `App.jsx`, para que
 * las dos formas de terminar una sesión dejen el `localStorage` en el mismo estado.
 */
const clearStoredSession = (): void => {
  localStorage.removeItem(STORAGE_KEYS.USER);
  localStorage.removeItem(STORAGE_KEYS.TOKEN);
};

/**
 * Recarga en la pantalla de login en vez de sólo navegar, para que no quede viva ninguna
 * consulta de la sesión anterior. No se pierde nada editable: sin sesión no había forma de
 * guardarlo.
 */
const redirectToSignIn = (): void => {
  window.location.hash = "#/";
  window.location.reload();
};

/**
 * Avisa igual que el corte por inactividad de `App.jsx`: modal explicando qué pasó, y recién
 * cuando el colaborador lo acepta se lo lleva al login. Que las dos formas de terminar una
 * sesión se sientan iguales es lo que hace que "expiró" se lea como algo previsto y no como
 * una falla del portal.
 *
 * Silenciar `ErrorServer` antes del modal no es opcional: la misma respuesta 401 sigue viaje
 * hasta el módulo, y sweetalert2 muestra un modal por vez, así que ese popup le pisaría el
 * aviso al colaborador.
 *
 * No llama a `auth/logout`: la sesión del backend es stateless, ese endpoint sólo limpia un
 * contexto que igual se descarta al terminar la request. Con el token ya vencido no dejaría
 * ni rastro de auditoría.
 */
const announceExpiredSession = (): void => {
  suppressServerErrorPopups();
  InfoAlert(i18next.t("sessionExpiredTitle"), i18next.t("sessionExpired")).then(redirectToSignIn);
};

/**
 * Interceptor de response: cierra la sesión cuando el backend contesta 401.
 *
 * Sin esto el token vencido queda en `localStorage` para siempre — el corte por inactividad
 * sólo limpia si la pestaña quedó abierta — y `PrivateRoute`, que sólo mira que el token no
 * sea vacío, sigue dejando entrar. En prod se vieron tokens de hasta 23 días rebotando así.
 *
 * La guarda de `readUserToken()` hace de deduplicador: una carga de página dispara sus XHR en
 * paralelo y los 401 llegan en ráfaga, pero el primero deja el storage vacío y los siguientes
 * ya no encuentran sesión que cerrar.
 *
 * `closeSession` se inyecta para que los tests verifiquen la decisión sin abrir un modal ni
 * hacer navegar a jsdom.
 */
export const createUnauthorizedHandler =
  (closeSession: () => void = announceExpiredSession) =>
  (error: AuthErrorResponse): Promise<never> => {
    const isExpiredSession =
      error?.response?.status === 401 && !isAuthEndpoint(error?.config?.url) && readUserToken() !== null;

    if (isExpiredSession) {
      clearStoredSession();
      closeSession();
    }

    return Promise.reject(error);
  };

/** Registra el interceptor de auth en las instancias axios indicadas. */
export const registerAuthInterceptors = (...instances: AxiosInstance[]): void => {
  instances.forEach((instance) => {
    // El interceptor solo lee y escribe `headers`, y es estructuralmente compatible con lo
    // que axios pasa. El cast existe porque `InternalAxiosRequestConfig["headers"]` es la
    // clase `AxiosHeaders`, y tiparlo asi obligaria a reescribir los tests que lo ejercitan
    // con objetos planos.
    instance.interceptors.request.use(
      attachAuthHeaders as unknown as (config: InternalAxiosRequestConfig) => InternalAxiosRequestConfig,
    );
    instance.interceptors.response.use((response) => response, createUnauthorizedHandler());
  });
};
